import React, { useState } from 'react';
import Type from 'prop-types';
import { Form, Formik } from 'formik';
import { noop } from 'lodash';
import { Button, CircularProgress, IconButton } from '@mui/material';
import { ArrowBackIos } from '@mui/icons-material';
import { useTranslation } from 'hooks';
import { validStep } from 'utils/customProps';
import styles from './MultiStepForm.module.css';

const MultiStepForm = ({
  steps,
  initialValues,
  onEachStepSubmit,
  onFinalSubmit,
}) => {
  const [state, setState] = useState({
    errorMessage: '',
    stepNumber: 0,
  });
  const { t } = useTranslation();

  const { stepNumber } = state;

  const isFirstStep = stepNumber === 0;
  const isLastStep = () => {
    return stepNumber === steps.length - 1;
  };
  const CurrentStep = steps[stepNumber];

  const showNextStep = ({ setFieldTouched }) => {
    const nextStepFieldNames = Object.keys(steps[stepNumber + 1].initialValues);
    nextStepFieldNames.forEach((fieldName) =>
      setFieldTouched(fieldName, false),
    );
    setState((prevState) => ({
      ...prevState,
      stepNumber: prevState.stepNumber + 1,
    }));
  };

  const showPreviousStep =
    ({ setFieldTouched }) =>
    () => {
      const previousStepFieldNames = Object.keys(
        steps[stepNumber - 1].initialValues,
      );
      previousStepFieldNames.forEach((fieldName) =>
        setFieldTouched(fieldName, false),
      );

      setState((prevState) => ({
        ...prevState,
        stepNumber: prevState.stepNumber - 1,
      }));
    };

  const handleSubmit = async (values, formikBag) => {
    const errors = await formikBag.validateForm();

    if (Object.keys(errors).length > 0) {
      formikBag.setSubmitting(false);
      return;
    }

    try {
      await onEachStepSubmit(values);
      const currentStepSubmitHandler = steps[stepNumber].submitHandler;
      const responseData = await currentStepSubmitHandler(values);

      if (isLastStep()) {
        await onFinalSubmit(values, responseData?.data);
      } else {
        formikBag.setSubmitting(false);
        showNextStep(formikBag);
      }
    } catch (error) {
      formikBag.setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={CurrentStep.validationSchema}
      onSubmit={handleSubmit}
    >
      {(formikBag) => (
        <Form className={styles.form} method="post" noValidate>
          <CurrentStep {...formikBag} />
          {!isFirstStep && (
            <IconButton
              className={styles.prevButton}
              onClick={showPreviousStep(formikBag)}
              aria-label={t('back')}
            >
              <ArrowBackIos />
            </IconButton>
          )}

          <div className={styles.buttonWrapper}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={styles.nextButton}
              disabled={formikBag.isSubmitting}
            >
              {CurrentStep.buttonLabel ?? t('next')}
            </Button>

            {formikBag.isSubmitting && (
              <CircularProgress size={24} className={styles.buttonProgress} />
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

MultiStepForm.propTypes = {
  initialValues: Type.object.isRequired, // eslint-disable-line react/forbid-prop-types
  onEachStepSubmit: Type.func,
  onFinalSubmit: Type.func,
  steps: Type.arrayOf(validStep).isRequired,
};

MultiStepForm.defaultProps = {
  onEachStepSubmit: noop,
  onFinalSubmit: noop,
};

export default MultiStepForm;
